import { useMemo } from 'react';
import { useParams } from 'react-router';
import { EscalationStatus, QuoteStatus } from 'shared';

import { initialState as actionParamsInitialState } from '../../../../hooks/useActions';
import { useContactQuotes } from '../../../../hooks/useContactQuotes';
import { useFetchActionsQuery } from '../../../../services/api/actionApi/action';
import { useFetchEscalationsQuery } from '../../../../services/api/escalationApi/escalation';
import { useFetchInvoicesQuery } from '../../../../services/api/invoiceApi/invoice';

function useRouteIds() {
  const { organisationId, contactId } = useParams();

  return { organisationId, contactId: Number(contactId) };
}

/**
 * Every escalation for the contact, any status, including ones whose invoices are paid.
 * The table hooks can't be used here, they take status and date filters from the table models
 */
export function useContactAllEscalations() {
  const { organisationId, contactId } = useRouteIds();
  const result = useFetchEscalationsQuery({
    organisationId,
    contactId,
    includePaid: true,
    limit: 100,
  }, { skip: !organisationId || !contactId });

  const escalations = useMemo(() => result.data?.escalations || [], [result.data]);
  const open = useMemo(() => escalations.filter(e => e.escalationStatus === EscalationStatus.PENDING), [escalations]);

  return { ...result, escalations, open };
}

/** Pending, completed and failed actions across the contact and its invoices, newest first */
export function useContactAllActions() {
  const { organisationId, contactId } = useRouteIds();

  // ponytail: newest 300 only, older actions stay in the Actions tab
  return useFetchActionsQuery({
    ...actionParamsInitialState,
    organisationId,
    contactId,
    limit: 300,
    sortBy: `actionOn`,
    sortOrder: `desc`,
    // false would hide actions from manual triggers (system policies)
    systemActions: undefined,
  }, { skip: !organisationId || !contactId });
}

/** Unpaid invoices for the contact, oldest due first */
export function useContactUnpaidInvoices() {
  const { organisationId, contactId } = useRouteIds();
  // ponytail: one page of 200, the ageing breakdown hides when a contact has more. Sum per bucket server side if that matters
  const result = useFetchInvoicesQuery({
    organisationId,
    contactId,
    xeroStatus: `AUTHORISED`,
    page: 1,
    limit: 200,
  }, { skip: !organisationId || !contactId });

  const invoices = useMemo(() => [...(result.data?.invoices || [])]
    .map(item => item.invoice)
    .sort((a, b) => (a.dueDate || ``).localeCompare(b.dueDate || ``)), [result.data]);

  // More unpaid invoices than one page holds
  const truncated = (result.data?.count || 0) > invoices.length;

  return { ...result, invoices, truncated };
}

/** Sent or accepted quotes, the ones still waiting on the customer */
export function useContactOpenQuotes() {
  const { quotes } = useContactQuotes();

  return useMemo(() => [
    ...(quotes[QuoteStatus.SENT] || []),
    ...(quotes[QuoteStatus.ACCEPTED] || []),
  ], [quotes]);
}
